import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { Row, Column } from 'react-foundation';
import { connect } from 'react-redux';
import { Button } from '../../components/Button';
import { Counter } from '../../components/Counter';
import { InputField } from '../../components/InputField';
import { Error as ErrorView } from '../../components/Error';
import { Link } from 'react-router-dom';
import { selectErrors } from '../../modules/global/selectors';
import { Delivery } from '../../model/Account';
import { validate } from '../../utils/validators';
import { OfferingType } from '../../utils/offeringType';
import { ApiRequest } from '../../utils/api';
import { getAccountId } from '../../utils/helpers';
import { useApiQuery, useApiMutation } from '../../utils/apiHooks';
import { Checkbox } from '../../components/Checkbox';
import { gaB2BOrderMoreSims } from '../../utils/analytics';
import businessProducts from '../../utils/products';
import IconInfo from '../../static/images/icon_info.svg';

interface Props {
	errors: any[];
	orderSimsFinished: boolean;
	offeringType: OfferingType;
}

const Container = styled.div`
	margin: auto;
	max-width: 18rem;
	text-align: left;
`;

const ButtonContainer = styled.div`
	margin: auto;
	max-width: 18rem;
	text-align: center;
`;

const Centralize = styled.div`
	text-align: center;
	margin-bottom: 3rem;
`;

const Phead = styled.h1`
	margin-top: 4rem;
	text-align: center;
	font-size: 2.8rem;
`;

const Subhead = styled.h4`
	font-size: 1.8rem;
	margin-bottom: 1px;
	text-align: center;
	margin-top: 3.5rem;
`;

const TotalPrice = styled.div`
	h4 {
		font-size: 3.2rem;
		margin: 0;
		padding: 0;
	}

	p {
		margin-top: 0;
	}
`;

const ConfirmationText = styled.div`
	margin: auto;
	max-width: 40rem;
	text-align: center;
	h4 {
		font-size: 3.2rem;
	}
`;

const ErrorMessage = styled.p`
	font-size: 1rem;
	color: red;
`;

const EsimInfo = styled.div`
	display: flex;
	align-items: center;
	max-width: 400px;
	color: #ff0000;
	line-height: 1.3;
	text-align: left;
	margin: 1.25rem auto 0;
	
	a {
		color: #ff0000;
		text-decoration: underline;
		
		&:hover {
			
		}
	}
	
	img {
		flex: 0 0 24px;
		margin-right: 15px;
	}
	
	div {
		flex: 1 1 auto;
	}
`;


const MIN_COUNT = 1;
const MAX_COUNT = 200;

export const MoreSimsPageImpl: React.FC<Props> = (props: Props) => {
	const [count, setCount] = useState<number | undefined>(MIN_COUNT);
	const [submitted, setSubmitted] = useState(false);
	const [deliveryAddress, setDeliveryAddress] = useState<Delivery | undefined>(undefined);
	const [updateDeliveryAddress, setUpdateDeliveryAddress] = useState(false);
	const [validationErrors, setValidationErrors] = useState(false);

	const { data: customerData } = useApiQuery(ApiRequest.loadCustomerData(getAccountId()));
	const [submitOrder, { isSuccess: submitOrderSuccess }] = useApiMutation((order: any) => ApiRequest.orderSims(getAccountId(), order));
	const [submitDeliveryAddress] = useApiMutation((updates: any) => ApiRequest.updateCustomer(updates, getAccountId()));

	useEffect(() => {
		if (customerData !== undefined) {
			setDeliveryAddress(customerData.delivery);
		}
	}, [customerData]);

	const onAddressChange = (field: string, value: string) => {
		const newState = { ...deliveryAddress };
		(newState as any)[field] = value;
		setDeliveryAddress(newState);
		setValidationErrors(false); // there could be a validation error, but currently we show that only when the submit button is clicked
	};

	const validateOrder = (order: any) =>
		!_.values(order.address).some((addr) => _.isNil(addr)) &&
		validate(order.address.firstName, 'notEmpty') &&
		validate(order.address.address, 'notEmpty') &&
		validate(order.address.postalCode, 'postalCode') &&
		validate(order.address.city, 'notEmpty') &&
		order.count >= MIN_COUNT &&
		order.count <= MAX_COUNT;

	const handleSubmit = () => {
		const address = {
			firstName: deliveryAddress!.recipientName,
			address: deliveryAddress!.streetAddress,
			postalCode: deliveryAddress!.postalCode,
			city: deliveryAddress!.city
		};
		const order = {
			count: count,
			address
		};

		if (validateOrder(order)) {
			submitOrder(order);

			if (updateDeliveryAddress) {
				const updates = [];
				updates.push(
					{
						field: 'delivery-first-name',
						value: address.firstName
					},
					{
						field: 'delivery-street-address',
						value: address.address
					},
					{
						field: 'delivery-postal-code',
						value: address.postalCode
					},
					{
						field: 'delivery-city',
						value: address.city
					}
				);
				submitDeliveryAddress({ updates: updates });
			}
			setSubmitted(true);
			gaB2BOrderMoreSims(count, businessProducts.getSimInitialPrice());
			document.body.scrollTop = document.documentElement.scrollTop = 0;
		} else {
			setValidationErrors(true);
		}
	};

	const containsOrderError = (errors: any[]) => {
		if (!errors || errors.length === 0) {
			return false;
		}
		let foundError = false;
		errors.forEach((error) => {
			const isOrderEndpoint = new RegExp('^/sims/order/.*$').test(error.endpointAndQueryParameters);
			if (isOrderEndpoint) {
				foundError = true;
			}
		});
		return foundError;
	};

	const onCounterChange = (value: number | undefined) => {
		setCount(value);
		setValidationErrors(false); // there could be a validation error, but currently we show that only when the submit button is clicked
	};

	const createCounterValidationError = (value: number) => {
		if (value > MAX_COUNT) {
			return `Simmien maksimi määrä webbikaupasta on ${MAX_COUNT}. Jos haluat tilata enemmän, otathan yhteyttä asiakaspalveluumme.`;
		} else if (value < MIN_COUNT) {
			return `Simmejä voi tilata ${MIN_COUNT} - ${MAX_COUNT} kappaletta.`;
		} else {
			throw new Error('assertion failed');
		}
	};

	const getSimCountWord = (count: number | undefined) => {
		if (count !== 1) {
			return 'simmiä';
		} else {
			return 'simmi';
		}
	};

	const { errors } = props;
	const ready = deliveryAddress !== undefined;
	const noErrors = errors && errors.length < 1;
	const orderError = containsOrderError(errors);
	const priceCalculationSimCount = count !== undefined ? count : 0;
	const priceText = `${priceCalculationSimCount * 2} €`;
	const priceDescription = `${priceCalculationSimCount} ${getSimCountWord(priceCalculationSimCount)} x 2€. Hinta sisältää toimituskulut.`;
	return (
		<article>
			<Helmet title="Tilaa lisää simmejä" meta={[{ name: 'description', content: '' }]} />
			{noErrors && ready && !submitted && (
				<div>
					<Phead>Tilaa lisää simmejä</Phead>
					<Subhead>Montako uutta simmiä tarvitset?</Subhead>
					<Centralize>
						Jos olet tilannut jo uusia simmejä ja saanut niistä tilausvahvistuksen, ne näkyvät pian simmivarastossa ja saapuvat postissa muutaman arkipäivän sisällä.
						<EsimInfo>
							<img src={IconInfo} alt="info"/>
							<div>
								Tarvitsetko liittymällesi digitaalisen eSIM-kortin? <br />
								<a href="#/account/activateSubscription">Tästä pääset sellaisen tilaamaan.</a>
							</div>
						</EsimInfo>
					</Centralize>
					<Row>
						<Column className="columns 4">
							<Centralize>
								<Counter value={count} minValue={MIN_COUNT} maxValue={MAX_COUNT} onChange={onCounterChange} createValidationError={createCounterValidationError} />
							</Centralize>
						</Column>
					</Row>
					<Centralize>
						<Subhead>Hinta yhteensä</Subhead>
						<TotalPrice>
							<h4>{priceText}</h4>
							<p>{priceDescription}</p>
						</TotalPrice>
						<Subhead>Toimitusosoite</Subhead>
						<Container>
							<InputField title={'Etu- ja sukunimi'} field={'recipientName'} onChange={onAddressChange} disabled={false} value={deliveryAddress!.recipientName} />
							<InputField title={'Katuosoite'} field={'streetAddress'} onChange={onAddressChange} disabled={false} value={deliveryAddress!.streetAddress} />
							<InputField title={'Postinumero'} field={'postalCode'} onChange={onAddressChange} disabled={false} value={deliveryAddress!.postalCode} />
							<InputField title={'Postitoimipaikka'} field={'city'} onChange={onAddressChange} disabled={false} value={deliveryAddress!.city} />
							{validationErrors && <ErrorMessage>Tarkista kentät</ErrorMessage>}
							<Checkbox messageWidth={'70%'} checked={updateDeliveryAddress} message={<span>Tallenna oletusosoitteeksi</span>} onChange={() => setUpdateDeliveryAddress(!updateDeliveryAddress)} />
						</Container>
					</Centralize>
				</div>
			)}
			{noErrors && ready && !submitted && (
				<ButtonContainer>
					<Button className="mini" onClick={handleSubmit}>
						Lähetä tilaus
					</Button>
					<Link to="/account/dashboard">
						<Button className="mini invisible"> Enpäs</Button>
					</Link>
				</ButtonContainer>
			)}
			{/* // TODO: When error modal is removed, we need to modify this, so that we communicate address save failed case to user */}
			{!orderError && submitted && submitOrderSuccess && (
				<ConfirmationText>
					<h4>{`${count} ${getSimCountWord(count)} tilattu!`}</h4>
					<p>Simmien toimitusaika on noin 3 arkipäivää. Veloitamme tilaamasi simmit (2 €/kpl, sis. toimituskulut) erillisellä laskulla.</p>
					<Link to="/account/activateSubscription">
						<Button className="gradient mini"> Ok </Button>
					</Link>
				</ConfirmationText>
			)}
			{orderError && <ErrorView message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />}
		</article>
	);
};

const mapStateToProps = (state: any) => {
	const errors = selectErrors(state);
	return {
		errors
	};
};

export default connect(mapStateToProps)(MoreSimsPageImpl);
